import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import { InventoryItem } from '../../types';
import { DataTable } from '../../components/common/DataTable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Modal } from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import { ColumnDef } from '@tanstack/react-table';
import { Package, AlertTriangle, Edit3, CheckCircle2, Plus, ImageOff } from 'lucide-react';

// Category is an admin-only grouping tag for stock (form factor / power). The app
// does NOT read this to decide how a camera connects — it already owns the device
// and its own pairing flow — so it's purely for how you sort the catalog here.
const CATEGORY = ['Outdoor', 'Indoor', 'PTZ', 'Solar', '4G', 'WiFi', 'PoE'];
const emptyAdd = { modelName: '', sku: '', type: 'Outdoor', price: 0, quantity: 0, lowStockThreshold: 10, description: '' };

export const InventoryPage: React.FC = () => {
  const { can } = useAuth();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [editQty, setEditQty] = useState<number>(0);
  const [editThreshold, setEditThreshold] = useState<number>(0);
  const [editPrice, setEditPrice] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add camera model
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ ...emptyAdd });
  const [addImage, setAddImage] = useState<File | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const handleAddCamera = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    if (!addForm.modelName.trim() || !addForm.sku.trim()) { setAddError('Camera name and model number are required.'); return; }
    setAddBusy(true);
    try {
      const id = await api.createInventoryItem({
        modelName: addForm.modelName.trim(),
        sku: addForm.sku.trim(),
        type: addForm.type,
        price: addForm.price,
        quantity: addForm.quantity,
        lowStockThreshold: addForm.lowStockThreshold,
        description: addForm.description.trim(),
      });
      if (addImage && id) {
        try { await api.uploadInventoryImage(id, addImage); }
        catch { /* item created; image can be added later via Adjust */ }
      }
      setIsAddOpen(false);
      setAddForm({ ...emptyAdd });
      setAddImage(null);
      loadInventory();
    } catch (err: any) {
      setAddError(err?.message || 'Could not add the camera model.');
    } finally {
      setAddBusy(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    try {
      const data = await api.getInventory();
      setInventory(data);
    } catch (err) {
      console.error('Failed to load inventory', err);
    }
  };

  const handleEditOpen = (item: InventoryItem) => {
    setSelectedItem(item);
    setEditQty(item.quantityOnHand);
    setEditThreshold(item.lowStockThreshold);
    setEditPrice(item.retailPriceUsd);
    setError(null);
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateInventoryItem(selectedItem.id, {
        quantityOnHand: editQty,
        lowStockThreshold: editThreshold,
        retailPriceUsd: editPrice,
      });
      setSelectedItem(null);
      loadInventory();
    } catch (err: any) {
      setError(err?.message || 'Failed to update inventory');
    } finally {
      setSaving(false);
    }
  };

  const totalInventoryValue = inventory.reduce(
    (acc, i) => acc + i.quantityOnHand * i.retailPriceUsd,
    0
  );
  const lowStockCount = inventory.filter((i) => i.quantityOnHand <= i.lowStockThreshold).length;

  const columns = useMemo<ColumnDef<InventoryItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Hardware Item / SKU',
        cell: (info) => {
          const item = info.row.original;
          // Accept a full URL (uploaded to storage) or a same-origin path such
          // as /camera-images/the-eye-4g.png (the seeded catalog photos bundled
          // with the dashboard). A bare asset name like "the-eye-4g" is NOT a
          // loadable src, so it still falls back to the placeholder.
          const hasImg = !!item.imageUrl && /^(https?:\/\/|\/)/.test(item.imageUrl);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {hasImg ? (
                  <img src={item.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <ImageOff size={14} color="var(--text-dim)" />
                )}
              </div>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{item.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                  Model #: <code>{item.sku}</code>{item.model ? ` • ${item.model}` : ''}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: (info) => (
          <span style={{ textTransform: 'capitalize', fontSize: '12px' }}>
            {info.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: 'quantityOnHand',
        header: 'Stock On Hand',
        cell: (info) => {
          const item = info.row.original;
          const isLow = item.quantityOnHand <= item.lowStockThreshold;
          return (
            <div>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: '14px',
                  color: isLow ? 'var(--danger-text)' : 'var(--text-main)',
                }}
              >
                {item.quantityOnHand} units
              </span>
              {isLow && (
                <div style={{ fontSize: '11px', color: 'var(--danger-text)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <AlertTriangle size={12} />
                  <span>Low stock (below {item.lowStockThreshold})</span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'retailPriceUsd',
        header: 'Retail Price',
        cell: (info) => (
          <span style={{ fontWeight: 600 }}>${(info.getValue() as number).toFixed(2)}</span>
        ),
      },
      {
        id: 'totalValuation',
        header: 'Asset Value',
        cell: (info) => {
          const item = info.row.original;
          return (
            <span style={{ color: 'var(--text-muted)' }}>
              ${(item.quantityOnHand * item.retailPriceUsd).toFixed(2)}
            </span>
          );
        },
      },
      {
        accessorKey: 'updatedAt',
        header: 'Last Restock',
        cell: (info) => (
          <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>
            {new Date(info.getValue() as string).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => handleEditOpen(info.row.original)}
            disabled={!can('devices:update')}
          >
            <Edit3 size={13} />
            <span>Adjust</span>
          </button>
        ),
      },
    ],
    [can]
  );

  return (
    <div>
      {/* Top Banner with Stock Metrics */}
      <div
        style={{
          background: 'var(--bg-surface-card)',
          border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)',
          padding: '16px 24px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>
            Hardware & Cellular Stock Inventory
          </h2>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Tracks physical camera stock, cellular M2M SIM cards, and accessories available for customer shipment.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '20px' }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-dim)', textTransform: 'uppercase' }}>
              Total Asset Valuation
            </div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-main)' }}>
              ${totalInventoryValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div style={{ borderLeft: '1px solid var(--border-subtle)', paddingLeft: '20px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-dim)', textTransform: 'uppercase' }}>
              Low Stock Alerts
            </div>
            <div
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: lowStockCount > 0 ? 'var(--danger-text)' : 'var(--success-text)',
              }}
            >
              {lowStockCount} items
            </div>
          </div>
        </div>
      </div>

      {can('devices:update') && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <button className="btn btn-primary btn-sm" onClick={() => { setAddForm({ ...emptyAdd }); setAddImage(null); setAddError(null); setIsAddOpen(true); }}>
            <Plus size={14} /><span>Add camera</span>
          </button>
        </div>
      )}

      <DataTable
        storageKey="inventory"
        data={inventory}
        columns={columns}
        searchPlaceholder="Search camera name, model number..."
        exportFilename="mwm_hardware_inventory.csv"
      />

      {/* Adjust Inventory Stock Modal */}
      <Modal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title={`Adjust Stock: ${selectedItem?.name}`}
        subtitle={`SKU: ${selectedItem?.sku} • Model: ${selectedItem?.model}`}
      >
        <form onSubmit={handleSaveItem}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Quantity On Hand (Units)
              </label>
              <input
                type="number"
                min="0"
                className="input"
                style={{ width: '100%' }}
                value={editQty}
                onChange={(e) => setEditQty(parseInt(e.target.value) || 0)}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Low-Stock Warning Threshold
              </label>
              <input
                type="number"
                min="1"
                className="input"
                style={{ width: '100%' }}
                value={editThreshold}
                onChange={(e) => setEditThreshold(parseInt(e.target.value) || 1)}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
              Retail Price ($ USD)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input"
              style={{ width: '100%' }}
              value={editPrice}
              onChange={(e) => setEditPrice(parseFloat(e.target.value) || 0)}
              required
            />
          </div>

          {error && <div style={{ color: 'var(--danger-text)', fontSize: 12, marginBottom: 12 }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedItem(null)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              <CheckCircle2 size={14} />
              <span>{saving ? 'Saving…' : 'Save item'}</span>
            </button>
          </div>
        </form>
      </Modal>

      {/* Add camera model */}
      <Modal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} title="Add camera" subtitle="Adds a model to the admin catalog so you can track its stock here">
        <form onSubmit={handleAddCamera}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Camera name</label>
              <input type="text" className="input" style={{ width: '100%' }} value={addForm.modelName} onChange={(e) => setAddForm({ ...addForm, modelName: e.target.value })} placeholder="The Eye 4G" required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Model number</label>
              <input type="text" className="input" style={{ width: '100%', fontFamily: 'monospace' }} value={addForm.sku} onChange={(e) => setAddForm({ ...addForm, sku: e.target.value })} placeholder="TE-4G-01" required />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Category</label>
              <select className="select" style={{ width: '100%' }} value={addForm.type} onChange={(e) => setAddForm({ ...addForm, type: e.target.value })}>
                {CATEGORY.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Retail price ($)</label>
              <input type="number" min="0" step="0.01" className="input" style={{ width: '100%' }} value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: parseFloat(e.target.value) || 0 })} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Stock on hand</label>
              <input type="number" min="0" className="input" style={{ width: '100%' }} value={addForm.quantity} onChange={(e) => setAddForm({ ...addForm, quantity: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Low-stock threshold</label>
              <input type="number" min="1" className="input" style={{ width: '100%' }} value={addForm.lowStockThreshold} onChange={(e) => setAddForm({ ...addForm, lowStockThreshold: parseInt(e.target.value) || 10 })} />
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Description</label>
            <input type="text" className="input" style={{ width: '100%' }} value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} placeholder="4G LTE Outdoor Security Camera" />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Product image</label>
            <input type="file" accept="image/*" className="input" style={{ width: '100%' }} onChange={(e) => setAddImage(e.target.files?.[0] ?? null)} />
            <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>Use the model's image (e.g. the-eye-4g.png from the app). Optional — you can add it later.</div>
          </div>

          {addError && <div style={{ color: 'var(--danger-text)', fontSize: 12, marginBottom: 12 }}>{addError}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIsAddOpen(false)} disabled={addBusy}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={addBusy}>
              <Plus size={14} />
              <span>{addBusy ? 'Adding…' : 'Add camera'}</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
